import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Heading, Button, Checkbox, DatePicker, Picker, PickerItem, NumberField, Switch } from "@react-spectrum/s2";
import { toast } from '../../utils/toast';
import { parseDate, CalendarDate, today, getLocalTimeZone } from '@internationalized/date';
import { CCOrganization } from '../../types';
import { updateCCOrganization } from '../../services/api';
import { SurfaceCard } from '../common/SurfaceCard';
export interface OrgConfigurationFormProps {
    organization: CCOrganization;
    onSaved: () => void;
}
/**
 * "Configuration" tab body — type, subscription, usage limits, and
 * retrieval/publishing toggles. Matches design-frames/org-detail.html's
 * content-orgconfig, same fields the org card's Edit dialog used to hold.
 */ export const OrgConfigurationForm: React.FC<OrgConfigurationFormProps> = ({ organization, onSaved })=>{
    const { t } = useTranslation();
    const [orgType, setOrgType] = useState<'SANDBOX' | 'PROVISIONED'>(organization.type);
    const [subscriptionDate, setSubscriptionDate] = useState<CalendarDate | null>(null);
    const [requestLimit, setRequestLimit] = useState(organization.request_usage?.total ?? 0);
    const [documentLimit, setDocumentLimit] = useState(organization.document_indexed?.total ?? 0);
    const [domainLimit, setDomainLimit] = useState(organization.domain?.total ?? 0);
    const [useGuidedTraversal, setUseGuidedTraversal] = useState(organization.retrieval_algorithm === 'guided_traversal');
    const [publishingEnabled, setPublishingEnabled] = useState(organization.publishing_enabled ?? false);
    const [zipSizeLimitMb, setZipSizeLimitMb] = useState(organization.zip_size_limit_mb);
    const [pdfSizeLimitMb, setPdfSizeLimitMb] = useState(organization.pdf_size_limit_mb);
    const [maxDitaFilesPerZip, setMaxDitaFilesPerZip] = useState(organization.max_dita_files_per_zip);
    const [isSaving, setIsSaving] = useState(false);
    // A subscription end date in the past is invalid regardless of whether it
    // was just edited or arrived stale from the API: an already-expired date
    // must be corrected before the configuration can be saved. minValue keeps
    // the calendar picker to today-onwards; this check also covers dates typed
    // directly into the field segments.
    const todayDate = today(getLocalTimeZone());
    const subscriptionIsPast = !!subscriptionDate && subscriptionDate.compare(todayDate) < 0;
    useEffect(()=>{
        setOrgType(organization.type);
        try {
            const loaded = organization.subscription_end ? parseDate(organization.subscription_end.split('T')[0]) : null;
            setSubscriptionDate(loaded ?? today(getLocalTimeZone()));
        } catch  {
            setSubscriptionDate(today(getLocalTimeZone()));
        }
        setRequestLimit(organization.request_usage?.total ?? 0);
        setDocumentLimit(organization.document_indexed?.total ?? 0);
        setDomainLimit(organization.domain?.total ?? 0);
        setUseGuidedTraversal(organization.retrieval_algorithm === 'guided_traversal');
        setPublishingEnabled(organization.publishing_enabled ?? false);
        setZipSizeLimitMb(organization.zip_size_limit_mb);
        setPdfSizeLimitMb(organization.pdf_size_limit_mb);
        setMaxDitaFilesPerZip(organization.max_dita_files_per_zip);
    }, [
        organization
    ]);
    const handleSave = useCallback(async ()=>{
        if (!subscriptionDate || subscriptionIsPast) return;
        setIsSaving(true);
        try {
            await updateCCOrganization(organization.org_id, {
                type: orgType,
                subscription_end: subscriptionDate.toString(),
                request_limit: requestLimit,
                document_index_limit: documentLimit,
                domain_limit: domainLimit,
                retrieval_algorithm: useGuidedTraversal ? 'guided_traversal' : 'template_traversal',
                publishing_enabled: publishingEnabled,
                zip_size_limit_mb: zipSizeLimitMb,
                pdf_size_limit_mb: pdfSizeLimitMb,
                max_dita_files_per_zip: maxDitaFilesPerZip
            });
            toast.positive(t('commandCenter.orgConfigForm.saved'));
            onSaved();
        } finally{
            setIsSaving(false);
        }
    }, [
        organization.org_id,
        orgType,
        subscriptionDate,
        subscriptionIsPast,
        requestLimit,
        documentLimit,
        domainLimit,
        useGuidedTraversal,
        publishingEnabled,
        zipSizeLimitMb,
        pdfSizeLimitMb,
        maxDitaFilesPerZip,
        onSaved,
        t
    ]);
    return (<div className={" sd171 _ta171 Ul171 ql171 Lz6EEyb171"}>
      <SurfaceCard stack>
        <Heading level={4} styles={" Jy171 Gy171 Iy171 Hy171"}>{t('commandCenter.orgConfigForm.heading')}</Heading>

        <Picker label={t('commandCenter.orgConfigForm.orgType')} value={orgType} onChange={(key)=>setOrgType(key as 'SANDBOX' | 'PROVISIONED')} styles={" Za171"}>
          <PickerItem id="PROVISIONED" key="PROVISIONED">{t('commandCenter.orgConfigForm.provisioned')}</PickerItem>
          <PickerItem id="SANDBOX" key="SANDBOX">{t('commandCenter.orgConfigForm.sandbox')}</PickerItem>
        </Picker>

        <DatePicker label={t('commandCenter.orgConfigForm.subscriptionEndDate')} value={subscriptionDate} onChange={setSubscriptionDate} minValue={todayDate} isInvalid={subscriptionIsPast} errorMessage={t('commandCenter.orgConfigForm.subscriptionPastError')} styles={" Za171"}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxQueries')} value={requestLimit} onChange={setRequestLimit} minValue={0} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxIndexedFiles')} value={documentLimit} onChange={setDocumentLimit} minValue={0} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxDomains')} value={domainLimit} onChange={setDomainLimit} minValue={0} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxZipSize')} value={zipSizeLimitMb} onChange={setZipSizeLimitMb} minValue={1} maxValue={4096} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxPdfSize')} value={pdfSizeLimitMb} onChange={setPdfSizeLimitMb} minValue={1} maxValue={100} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>

        <NumberField label={t('commandCenter.orgConfigForm.maxDitaFiles')} value={maxDitaFilesPerZip} onChange={setMaxDitaFilesPerZip} minValue={1} styles={" Za171"} formatOptions={{
        useGrouping: true
    }}/>
      </SurfaceCard>

      <SurfaceCard stack>
        <Heading level={4} styles={" Jy171 Gy171 Iy171 Hy171"}>{t('commandCenter.orgConfigForm.retrievalPublishing')}</Heading>

        <div className={" sd171 _ta171 Ue171 qe171 Za171"}>
          <Text>{t('commandCenter.orgConfigForm.retrievalAlgorithm')}</Text>
          <Switch isSelected={useGuidedTraversal} onChange={setUseGuidedTraversal} aria-label={t('commandCenter.orgConfigForm.traversalAria')}>
            {useGuidedTraversal ? t('commandCenter.orgConfigForm.guidedTraversal') : t('commandCenter.orgConfigForm.templateTraversal')}
          </Switch>
        </div>

        <div className={" sd171 _ta171 Ue171 qe171 Za171"}>
          <Checkbox isSelected={publishingEnabled} onChange={setPublishingEnabled}>
            {t('commandCenter.orgConfigForm.allowPublishing')}
          </Checkbox>
        </div>
      </SurfaceCard>

      <div className={" sd171 _Cb171"}>
        <Button variant="accent" onPress={handleSave} isPending={isSaving} isDisabled={!subscriptionDate || subscriptionIsPast}>
          {t('commandCenter.orgConfigForm.saveChanges')}
        </Button>
      </div>
    </div>);
};
export default OrgConfigurationForm;

import "macro-e65a6cd981b335be1f7a9c3f0c4189ac4e5c125e5d662cb84d4ed6a7cd3745c2.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-35f0a3c880c5e3a070d26f81f30cabbad043bb17b0490e68a974358847af9fd4.css";
import "macro-35f0a3c880c5e3a070d26f81f30cabbad043bb17b0490e68a974358847af9fd4.css";
import "macro-267537f0fccf0d75b72fa9cdb8f24a18a54432f3aab47583e78e45cd0ed81b4c.css";